import type { Answers, BaseTierId, AddOnId, Recommendation } from "./types"
import { isAddOnOffered, isTierOffered, type QuizMarket } from "./market"

function pickBaseTier(a: Answers, market?: QuizMarket): BaseTierId {
  if (!a.mri_interest) return "blood_only"
  const wanted: BaseTierId = a.brain_interest ? "pro_mri" : "advanced_mri"
  if (!market) return wanted

  // Never recommend a tier the market doesn't offer. Degrade rather than
  // refuse: Pro without brain volumetry falls back to Advanced, and a market
  // with no MRI at all falls back to blood. A tier that is merely
  // `coming_soon` IS still recommended — the result page renders it with the
  // waitlist treatment (D3).
  if (isTierOffered(market, wanted)) return wanted
  if (wanted === "pro_mri" && isTierOffered(market, "advanced_mri")) {
    return "advanced_mri"
  }
  return "blood_only"
}

// Blood tests already bundled into the Pro tier. Recommending them as add-ons
// on top of Pro double-charges the customer — the booking app de-dupes bundled
// add-ons in its manual flow but NOT on URL preselect. Source of truth: the Pro
// package `components` in ahead-booking-new (07-mri-blood-changes.sql), which
// includes hormone + B-vitamins but deliberately excludes omega (kept as an
// add-on), so omega stays recommendable even for Pro.
const PRO_BUNDLED_ADD_ONS: ReadonlySet<AddOnId> = new Set([
  "hormone_test",
  "b_vitamins",
])

function pickAddOns(
  a: Answers,
  baseTier: BaseTierId,
  market?: QuizMarket
): AddOnId[] {
  const addOns: AddOnId[] = []
  if (a.hormone_interest) addOns.push("hormone_test")
  if (a.omega_interest) addOns.push("omega_test")
  if (a.b_vitamins_interest) addOns.push("b_vitamins")
  if (a.trend_interest) addOns.push("trend_overlay")
  if (a.mri_interest) {
    if (a.calcium_interest) addOns.push("calcium_score")
    if (a.dexa_interest) addOns.push("dexa_scan")
    if (
      a.mamma_interest &&
      a.gender === "female" &&
      typeof a.age === "number" &&
      a.age >= 30
    ) {
      addOns.push("mamma_mri")
    }
  }

  const deduped =
    baseTier === "pro_mri"
      ? addOns.filter((id) => !PRO_BUNDLED_ADD_ONS.has(id))
      : addOns

  // Imaging add-ons stay attached only while an MRI tier is actually
  // recommended — a Pro→blood degrade must not leave a DEXA scan behind.
  const mriDropped = baseTier === "blood_only"
  const withoutOrphans = mriDropped
    ? deduped.filter(
        (id) =>
          id !== "calcium_score" && id !== "dexa_scan" && id !== "mamma_mri"
      )
    : deduped

  if (!market) return withoutOrphans
  return withoutOrphans.filter((id) => isAddOnOffered(market, id, a.gender))
}

export function recommend(
  answers: Answers,
  market?: QuizMarket
): Recommendation {
  const baseTier = pickBaseTier(answers, market)
  return { baseTier, addOns: pickAddOns(answers, baseTier, market) }
}
